/**
 * Element runtimes: everything needed to draw one element at any time — its layers, stroke sets,
 * pen timelines and masks. Built once per element version (keyed by its JSON) and reused.
 */

import { makeCanvas, type Ctx2D } from "./canvas";
import type { CameraFn } from "./camera";
import { apply, chain, ease, rotate, scale, translate, type Mat } from "./math";
import type { Element, Project } from "./project.gen";
import { maskInto, MaskRevealer, putLayer, WipeRevealer } from "./reveal";
import type { Resources } from "./resources";
import { layoutShape } from "./shape";
import { buildStrokeSet, PenTimeline, zigzagStroke, type PenState, type PlanStroke } from "./strokes";
import { layoutText } from "./text";
import { enterDurations, type ElementTimes, type PhaseState } from "./timeline";

/** One pen-drawn phase: a layer revealed through a mask that follows a pen. */
export interface PenReveal {
  layer: CanvasImageSource;
  pen: PenTimeline | null;
  mask: MaskRevealer | WipeRevealer;
  /** wipe phases advance columns instead of points */
  wipe: boolean;
}

export interface ElementRuntime {
  el: Element;
  key: string;
  /** layer size, px */
  w: number;
  h: number;
  /** board units per layer pixel before the element's own scale */
  unit: number;
  /** first pen phase: outline / text writing / shape outline */
  first: PenReveal | null;
  /** second phase: fill hatching (images) or fill wipe (shapes) */
  second: PenReveal | null;
  /** what the element looks like once fully entered, bottom to top */
  layers: CanvasImageSource[];
  /** erase exit: an eraser scrubbing the layer away */
  eraser: { pen: PenTimeline; mask: MaskRevealer } | null;
  scratch: { canvas: OffscreenCanvas; ctx: Ctx2D };
}

const UNIT_TEXT_RES = 2; // layer pixels per board unit for text and shapes

export function elementKey(el: Element): string {
  return JSON.stringify(el);
}

export function buildRuntime(el: Element, res: Resources): ElementRuntime | null {
  const rt = buildBase(el, res);
  if (rt && el.exit.mode === "erase") {
    // eraser about 70 board units wide, scrubbing the layer's box
    const r = Math.max(rt.h / 10, 35 / Math.max(1e-6, rt.unit * el.transform.scale));
    const set = buildStrokeSet([zigzagStroke(0, 0, rt.w, rt.h, r)]);
    rt.eraser = { pen: new PenTimeline(set, { duration: Math.max(0.05, el.exit.duration), unitScale: rt.unit * el.transform.scale }), mask: new MaskRevealer(rt.w, rt.h, set) };
  }
  return rt;
}

function buildBase(el: Element, res: Resources): ElementRuntime | null {
  const d = enterDurations(el);
  const modeDraws = el.enter.mode === "draw" || el.enter.mode === "outline";

  if (el.type === "image" && el.image) {
    const img = res.images.get(el.image.assetId);
    if (!img) return null;
    const [w, h] = img.plan.size;
    const bs = el.image.brushScale;
    const xform = bs === 1 ? undefined : (x: number, y: number, r: number): [number, number, number] => [x, y, r * bs];
    const outlineStrokes = orderedGroups(img.plan.outline.groups, el.image.groupOrder);
    const fillStrokes = img.plan.fill.groups.flatMap((g) => g.strokes);
    const unit = 1;
    const oSet = buildStrokeSet(outlineStrokes, xform);
    const oPen = new PenTimeline(oSet, { duration: d.outline, unitScale: unit * el.transform.scale });
    const first: PenReveal = { layer: img.outline, pen: oPen, mask: new MaskRevealer(w, h, oSet), wipe: false };
    let second: PenReveal | null = null;
    if (el.enter.mode === "draw") {
      const fSet = buildStrokeSet(fillStrokes, xform);
      const fPen = new PenTimeline(fSet, {
        duration: d.fill,
        unitScale: unit * el.transform.scale,
        startAt: oPen.endPoint,
        maxTravelShare: 0.3,
        strokeOverhead: 0,
      });
      second = { layer: img.fill, pen: fPen, mask: new MaskRevealer(w, h, fSet), wipe: false };
    }
    const layers = el.enter.mode === "outline" ? [img.outline] : [img.fill, img.outline];
    return { el, key: elementKey(el), w, h, unit, first: modeDraws ? first : null, second, layers, eraser: null, scratch: makeCanvas(w, h) };
  }

  if (el.type === "text" && el.text) {
    const font = res.fonts.get(el.text.fontAssetId);
    if (!font) return null;
    const lay = layoutText(font.font, font.glyphs, el.text, UNIT_TEXT_RES);
    const unit = 1 / lay.res;
    let first: PenReveal | null = null;
    if (modeDraws) {
      if (el.text.writeMode === "wipe") {
        first = { layer: lay.layer, pen: null, mask: new WipeRevealer(lay.w, lay.h), wipe: true };
      } else {
        const set = buildStrokeSet(lay.strokes);
        const pen = new PenTimeline(set, {
          duration: d.outline,
          unitScale: unit * el.transform.scale,
          travelMin: 0.03,
          travelMax: 0.14,
          maxTravelShare: 0.3,
        });
        first = { layer: lay.layer, pen, mask: new MaskRevealer(lay.w, lay.h, set), wipe: false };
      }
    }
    return { el, key: elementKey(el), w: lay.w, h: lay.h, unit, first, second: null, layers: [lay.layer], eraser: null, scratch: makeCanvas(lay.w, lay.h) };
  }

  if (el.type === "shape" && el.shape) {
    const lay = layoutShape(el.shape, UNIT_TEXT_RES);
    const unit = 1 / lay.res;
    let first: PenReveal | null = null;
    let second: PenReveal | null = null;
    if (modeDraws) {
      const set = buildStrokeSet(lay.strokes);
      const pen = new PenTimeline(set, { duration: d.outline, unitScale: unit * el.transform.scale });
      first = { layer: lay.outline, pen, mask: new MaskRevealer(lay.w, lay.h, set), wipe: false };
      if (lay.fill && el.enter.mode === "draw") second = { layer: lay.fill, pen: null, mask: new WipeRevealer(lay.w, lay.h), wipe: true };
    }
    const layers = lay.fill && el.enter.mode !== "outline" ? [lay.fill, lay.outline] : [lay.outline];
    return { el, key: elementKey(el), w: lay.w, h: lay.h, unit, first, second, layers, eraser: null, scratch: makeCanvas(lay.w, lay.h) };
  }
  return null;
}

function orderedGroups(groups: { id: string; strokes: PlanStroke[] }[], order: string[] | null): PlanStroke[] {
  if (!order || order.length === 0) return groups.flatMap((g) => g.strokes);
  const byId = new Map(groups.map((g) => [g.id, g]));
  const seen = new Set<string>();
  const out: PlanStroke[] = [];
  for (const id of order) {
    const g = byId.get(id);
    if (g && !seen.has(id)) {
      out.push(...g.strokes);
      seen.add(id);
    }
  }
  for (const g of groups) if (!seen.has(g.id)) out.push(...g.strokes); // groups added by re-analysis
  return out;
}

// --------------------------------------------------------------------------------- placement

/** Layer pixels → board units, including move-in / move-off offsets at time t. */
export function elementMatrix(project: Project, rt: ElementRuntime, tm: ElementTimes, ph: PhaseState, cam: CameraFn): Mat {
  const tr = rt.el.transform;
  let [dx, dy] = [0, 0];
  if (ph.phase === "enter" && rt.el.enter.mode === "moveIn") {
    const off = offscreenOffset(project, rt, rt.el.enter.direction, cam(tm.enter1));
    const k = 1 - ease("easeOut", ph.u);
    dx = off[0] * k;
    dy = off[1] * k;
  } else if (ph.phase === "exit" && rt.el.exit.mode === "moveOff") {
    const off = offscreenOffset(project, rt, rt.el.exit.direction, cam(tm.exit0));
    const k = ease("easeIn", ph.u);
    dx = off[0] * k;
    dy = off[1] * k;
  }
  const s = tr.scale * rt.unit;
  return chain(
    translate(tr.x + dx, tr.y + dy),
    rotate(tr.rotation),
    scale(s * (tr.flipX ? -1 : 1), s * (tr.flipY ? -1 : 1)),
    translate(-rt.w / 2, -rt.h / 2),
  );
}

function offscreenOffset(project: Project, rt: ElementRuntime, dir: string, cam: { x: number; y: number; zoom: number }): [number, number] {
  const halfW = project.output.width / (2 * cam.zoom);
  const halfH = project.output.height / (2 * cam.zoom);
  const tr = rt.el.transform;
  const ew = (rt.w * rt.unit * tr.scale) / 2;
  const eh = (rt.h * rt.unit * tr.scale) / 2;
  const m = 40;
  switch (dir) {
    case "left":
      return [cam.x - halfW - ew - m - tr.x, 0];
    case "right":
      return [cam.x + halfW + ew + m - tr.x, 0];
    case "top":
      return [0, cam.y - halfH - eh - m - tr.y];
    default:
      return [0, cam.y + halfH + eh + m - tr.y];
  }
}

export function elementAlpha(rt: ElementRuntime, ph: PhaseState): number {
  let a = rt.el.opacity;
  if (ph.phase === "enter" && rt.el.enter.mode === "fade") a *= ease("easeInOut", ph.u);
  if (ph.phase === "exit" && rt.el.exit.mode === "fade") a *= 1 - ease("easeInOut", ph.u);
  return a;
}

// --------------------------------------------------------------------------------- drawing

/** Draw the element into ctx, whose transform already maps layer pixels to output pixels. Layers
 *  may be reduced editor copies (see resources.ts): putLayer stretches those to the layer size. */
export function drawElement(ctx: Ctx2D, rt: ElementRuntime, ph: PhaseState): void {
  if (ph.phase === "before" || ph.phase === "gone") return;
  if (ph.phase === "outline" && rt.first) {
    drawReveal(ctx, rt, rt.first, ph);
    return;
  }
  if (ph.phase === "fill" && rt.second) {
    // outline layer complete underneath... the fill goes below it, so draw fill first
    drawReveal(ctx, rt, rt.second, ph);
    if (rt.first) putLayer(ctx, rt.first.layer, rt.scratch.canvas);
    return;
  }
  if (ph.phase === "exit" && rt.eraser) {
    // the finished element, minus everything the eraser has scrubbed so far
    const sc = rt.scratch.ctx;
    rt.eraser.mask.update(rt.eraser.pen.at(ph.local).revealed);
    sc.save();
    sc.globalCompositeOperation = "copy";
    putLayer(sc, rt.layers[0], rt.scratch.canvas);
    sc.globalCompositeOperation = "source-over";
    for (let i = 1; i < rt.layers.length; i++) putLayer(sc, rt.layers[i], rt.scratch.canvas);
    sc.globalCompositeOperation = "destination-out";
    sc.drawImage(rt.eraser.mask.canvas, 0, 0);
    sc.restore();
    ctx.drawImage(rt.scratch.canvas, 0, 0);
    return;
  }
  for (const l of rt.layers) putLayer(ctx, l, rt.scratch.canvas);
}

/** Eraser position in layer pixels during an erase exit, or null. */
export function eraserTip(rt: ElementRuntime, ph: PhaseState): PenState | null {
  if (ph.phase !== "exit" || !rt.eraser) return null;
  return rt.eraser.pen.at(ph.local);
}

function drawReveal(ctx: Ctx2D, rt: ElementRuntime, r: PenReveal, ph: PhaseState): void {
  if (r.wipe) {
    (r.mask as WipeRevealer).update(ease("easeInOut", ph.u) * rt.w);
  } else if (r.pen) {
    (r.mask as MaskRevealer).update(r.pen.at(ph.local).revealed);
  }
  maskInto(rt.scratch.ctx, r.layer, r.mask.canvas);
  ctx.drawImage(rt.scratch.canvas, 0, 0);
}

/** Pen tip in layer pixels during a pen phase, or null. Wipes follow the wipe edge. */
export function penTip(rt: ElementRuntime, ph: PhaseState, t: number): (PenState & { phase: "first" | "second" }) | null {
  const r = ph.phase === "outline" ? rt.first : ph.phase === "fill" ? rt.second : null;
  if (!r) return null;
  const which = ph.phase === "outline" ? "first" : "second";
  if (r.wipe) {
    const x = ease("easeInOut", ph.u) * rt.w;
    const y = rt.h * (0.5 + 0.22 * Math.sin(t * 26));
    return { x, y, down: true, revealed: 0, phase: which };
  }
  if (!r.pen) return null;
  return { ...r.pen.at(ph.local), phase: which };
}

export function layerToBoard(m: Mat, x: number, y: number): [number, number] {
  return apply(m, x, y);
}
